export default {
  namespace: 'core',
  en: {
    appTitle: 'Exonet Portal',
    websiteTitle: 'Exonet Website',
    common: {
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      cancel: 'Cancel',
      all: 'All',
      enabled: 'Enabled',
      disabled: 'Disabled',
      save: 'Save',
      delete: 'Delete',
      active: 'Active',
      create: 'Create',
      createOfType: 'Create {{type}}',
      add: 'Add',
      addOfType: 'Add {{type}}',
      actions: 'Actions',
      none: 'None',
      notAvailable: 'Not available',
      edit: 'Edit',
      moreActions: 'More actions',
      copy: 'Copy {{type}}',
      copied: '{{type}} copied',
      value: 'Value',
      send: 'Send',
      noOptionsFound: 'No options found',
    },
    messages: {
      connectionLost: 'Live updates have been disabled due to inactivity.',
      sessionExpired: 'Your session has expired. To continue please login.',
      invalidLogin: 'Invalid email address and password combination.',
      ipNotAllowed: 'Your current IP address is not allowed to access the portal.',
      invalidSecret: 'The provided verification code is invalid.',
      tooManyRequests:
        'You have made too many requests in a short time. Please wait a moment and try again.',
      temporarilyUnavailable: 'The portal is temporarily unavailable. Please try again later.',
      maintenanceMode: 'The portal is currently in maintenance mode. Please try again later.',
      newVersion: {
        title: 'A new version of the portal is available',
        message: 'Click here to reload the page and use the latest version.',
      },
      // The errors that are shown in the banner of the layout.
      errors: {
        server: {
          title: 'Server error',
          message: 'The server could not process the request. Please try again later.',
        },
        network: {
          title: 'Connection error',
          message:
            'The connection to the server has been lost. Check your internet connection and try again.',
        },
        rateLimit: {
          title: 'Too many requests',
          message:
            'You have made too many requests in a short time. Please wait a moment and try again.',
        },
      },
    },
    alerts: {
      payAttention: 'Pay attention!',
    },
    error: {
      notFound: {
        title: 'Oops!',
        message: "We can't seem to find the page you're looking for.",
      },
      missingResource: {
        message: "This item doesn't exist (anymore) or you don't have access to it.",
        back: 'Back to {{title}}',
      },
      permission: {
        title: 'Access denied',
        message: "Sorry, but you don't have permission to access this page.",
      },
      exception: {
        title: 'Oops!',
        message: 'Something went wrong. Please try again later.',
        reference: 'Error Reference',
      },
      // Keyed by the error's `title` or `detail`, or by its `variables.rule`.
      api: {
        authorization: {
          notAllowed: 'You do not have permission for this action.',
        },
        validation: {
          phone:
            'This phone number is not valid. Check the full number, including the country code it starts with.',
        },
        rule: {
          Unique: 'This already exists, so it cannot be added again.',
        },
      },
    },
    menu: {
      main: 'Main',
      services: 'Services',
      security: 'Security',
      finance: 'Finance',
      toggle: 'Toggle',
      settings: 'Settings',
      loadingCustomer: 'Loading..',
      language: 'Language',
      provideFeedback: 'Provide feedback',
      search: {
        validation: 'Please enter a value.',
        placeholder: 'Search',
        submit: 'Go',
      },
      userSettings: 'User settings',
      theme: {
        title: 'Theme',
        automatic: 'Automatically switch theme based on system settings.',
        dark: {
          title: 'Dark',
          description: 'Dark mode',
        },
        light: {
          title: 'Light',
          description: 'Light mode',
        },
      },
      group: {
        customer: 'Customer',
        services: 'Services',
      },
      items: {
        services: 'Services',
        security: 'Security',
        overview: 'Overview',
        user: {
          title: 'User',
          settings: 'Settings',
          logout: 'Logout',
          switchCustomer: 'Switch customer',
        },
        customer: {
          title: 'Customer',
          details: 'Details',
          dashboard: 'Dashboard',
          contacts: 'Contacts',
          notifications: 'Email notifications',
          addresses: 'Addresses',
          quotes: 'Quotes',
        },
        support_tokens: 'Support tokens',
        secure_message: 'Secure message',
        order: {
          title: 'Order',
          domains: 'Domain names',
          certificates: 'Certificates',
        },
        users: {
          title: 'Users',
          roles: 'Roles',
        },
        tickets: {
          title: 'Tickets',
        },
        products: {
          title: 'Products',
        },
        finance: {
          title: 'Finance',
          invoices: 'Invoices',
        },
        domains: {
          title: 'Domain names',
          handles: 'Handles',
        },
        certificates: {
          title: 'Certificates',
        },
        dns: {
          title: 'DNS',
          bulkActions: 'Bulk actions',
        },
        servers: {
          title: 'Servers',
        },
        apps: {
          title: 'Apps',
        },
        secrets: {
          title: 'Secrets',
        },
        documents: {
          title: 'Documents',
        },
        actions: {
          title: 'Actions',
        },
        access: {
          ipWhitelist: 'IP Allowlist',
          customerPreferences: 'Customer preferences',
        },
        dashboard: {
          title: 'Dashboard',
        },
        settings: {
          title: 'Settings',
          appearance: 'Appearance',
          preferences: 'Preferences',
          security: 'Security',
          apiAccess: 'API Access',
          switchCustomer: 'Switch customer',
        },
      },
    },
    modals: {
      supportToken: {
        create: {
          title: 'Create support token',
          information:
            'A support token can be used to receive support without being an authorized contact. If you call us from a phone number or email us from an email address that is not known to us, you can still receive support from Exonet by using a support token.',
          description: 'Description',
          expiresAt: 'Expires at',
        },
        show: {
          tokenBelow: 'Your token is displayed below and will expire at ',
          copy: 'Copy token',
        },
      },
      secureMessage: {
        encrypt: {
          title: 'Encrypt a message',
          information:
            'A secure message is a safe way to send your sensitive data like passwords, tokens and certificates to Exonet. Your message will be encrypted and can not be decrypted without a key. Secure messages are only kept for a short period of time and destroyed afterwards.',
          displayInformation:
            'Your message has been encrypted and saved. We are only able to read this message if you provide us with the following key.',
          expiresAt: 'Expires at',
          message: 'Message',
          copyContent: 'Copy content',
          computedMessage: '#### Title\n**Username**: `root`  \n**Password**: `{{password}}`',
          shareableText: 'Verification code: {{code}}\nExpire date: {{date}}',
        },
      },
    },
    validation: {
      number: 'This value must be a number',
      maxChars: 'Maximum of {{amount}} characters allowed',
      required: 'This field is required',
      inFuture: 'The date should be in the future',
      validEmail: 'Please enter a valid email address',
      validLanguage: 'Please choose a valid language',
      validPhone: 'Please enter a valid phone number',
      validDate: 'Please enter a valid date',
      validValue: 'The selected value is not valid',
      validIp: 'Please enter a valid IP address',
      validZipCode: 'Please enter a valid zip code',
      minValue: 'The minimum value is {{min}}',
    },
    footer: {
      copyright: '© Exonet B.V. All specified prices exclude 21% VAT.',
      iso: 'Exonet is ISO 9001, ISO 27001 and NEN 7510 certified.',
      link: {
        status: {
          href: 'https://www.exonetstatus.com',
          title: 'Status',
        },
        terms: {
          href: 'https://www.exonet.nl/en/algemene-voorwaarden',
          title: 'General terms and conditions',
        },
        privacy: {
          href: 'https://www.exonet.nl/en/privacy-verklaring',
          title: 'Privacy statement',
        },
      },
    },
    dataManager: {
      title: 'Overview',
      search: {
        placeholder: 'Filter on..',
      },
      footer: {
        pagination: 'Showing $from to $to of $total.',
        info: 'Showing $amount of $total.',
      },
      noData: 'No results found.',
      noDataForType: 'No {{type}} found.',
      dateRange: {
        fromDate: 'From date',
        toDate: 'To date',
      },
      selectCountry: 'Select a country',
      export: {
        idle: 'Export',
        loading: 'Exporting…',
        success: 'Export completed',
        error: 'Export failed',
      },
    },
    select: {
      placeholder: 'Select an option',
      noOptionsFound: 'No options found',
    },
  },
  nl: {
    appTitle: 'Exonet Portal',
    websiteTitle: 'Exonet Website',
    common: {
      yes: 'Ja',
      no: 'Nee',
      close: 'Sluiten',
      cancel: 'Annuleren',
      all: 'Alles',
      enabled: 'Actief',
      disabled: 'Inactief',
      save: 'Opslaan',
      delete: 'Verwijderen',
      active: 'Actief',
      create: 'Toevoegen',
      createOfType: '{{type}} toevoegen',
      add: 'Toevoegen',
      addOfType: '{{type}} toevoegen',
      none: 'Geen',
      actions: 'Acties',
      edit: 'Wijzigen',
      moreActions: 'Meer acties',
      notAvailable: 'Niet beschikbaar',
      copy: '{{type}} kopiëren',
      copied: '{{type}} gekopieerd',
      value: 'Waarde',
      send: 'Versturen',
      noOptionsFound: 'Geen opties gevonden',
    },
    messages: {
      connectionLost: 'Live updates zijn uitgeschakeld vanwege inactiviteit.',
      sessionExpired: 'Je sessie is verlopen. Meld je opnieuw aan om verder te gaan.',
      invalidLogin: 'Ongeldige combinatie van e-mailadres en wachtwoord.',
      ipNotAllowed: 'Het huidige IP-adres heeft geen toegang tot de portal.',
      invalidSecret: 'De opgegeven verificatiecode is ongeldig.',
      tooManyRequests:
        'Je hebt te veel verzoeken in korte tijd gedaan. Wacht even en probeer het opnieuw.',
      temporarilyUnavailable: 'De portal is tijdelijk niet beschikbaar. Probeer het later opnieuw.',
      maintenanceMode: 'De portal is momenteel in onderhoud. Probeer het later opnieuw.',
      newVersion: {
        title: 'Er is een nieuwe versie van de portal beschikbaar',
        message: 'Klik hier om de pagina te herladen en de nieuwste versie te gebruiken.',
      },
      // The errors that are shown in the banner of the layout.
      errors: {
        server: {
          title: 'Serverfout',
          message: 'De server kon het verzoek niet verwerken. Probeer het later opnieuw.',
        },
        network: {
          title: 'Verbindingsfout',
          message:
            'De verbinding met de server is verbroken. Controleer je internetverbinding en probeer het opnieuw.',
        },
        rateLimit: {
          title: 'Te veel verzoeken',
          message:
            'Je hebt te veel verzoeken in korte tijd gedaan. Wacht even en probeer het opnieuw.',
        },
      },
    },
    alerts: {
      payAttention: 'Let op!',
    },
    error: {
      notFound: {
        title: 'Oeps!',
        message: 'We kunnen de pagina die je zoekt niet vinden.',
      },
      missingResource: {
        message: 'Dit item bestaat niet (meer) of je hebt er geen toegang toe.',
        back: 'Terug naar {{title}}',
      },
      permission: {
        title: 'Geen toegang',
        message: 'Sorry, maar je hebt geen toegang tot de aangevraagde pagina.',
      },
      exception: {
        title: 'Oeps!',
        message: 'Er is iets misgegaan. Probeer het later opnieuw.',
        reference: 'Foutreferentie',
      },
      // Op sleutel van de `title` of `detail` van de fout, of van `variables.rule`.
      api: {
        authorization: {
          notAllowed: 'Je hebt geen rechten voor deze actie.',
        },
        validation: {
          phone:
            'Dit telefoonnummer is niet geldig. Controleer het volledige nummer, inclusief het landnummer waarmee het begint.',
        },
        rule: {
          Unique: 'Dit bestaat al en kan niet nogmaals worden toegevoegd.',
        },
      },
    },
    menu: {
      toggle: 'Toggle',
      loadingCustomer: 'Laden..',
      language: 'Taal',
      main: 'Hoofdmenu',
      services: 'Diensten',
      security: 'Beveiliging',
      finance: 'Financieel',
      settings: 'Instellingen',
      provideFeedback: 'Geef feedback',
      userSettings: 'Gebruikersinstellingen',
      search: {
        validation: 'Vul een waarde in om te zoeken.',
        placeholder: 'Zoeken',
        submit: 'Zoek',
      },
      theme: {
        title: 'Thema',
        automatic: 'Automatisch thema wisselen op basis van systeeminstellingen.',
        dark: {
          title: 'Donker',
          description: 'Donkere modus',
        },
        light: {
          title: 'Licht',
          description: 'Lichte modus',
        },
      },
      group: {
        customer: 'Klant',
        services: 'Diensten',
      },
      items: {
        services: 'Diensten',
        security: 'Beveiliging',
        overview: 'Overzicht',
        user: {
          title: 'Gebruiker',
          settings: 'Instellingen',
          logout: 'Uitloggen',
          switchCustomer: 'Klant wisselen',
        },
        customer: {
          title: 'Klant',
          details: 'Gegevens',
          dashboard: 'Dashboard',
          contacts: 'Contactpersonen',
          notifications: 'Email notificaties',
          addresses: 'Adressen',
          quotes: 'Offertes',
        },
        support_tokens: 'Support tokens',
        secure_message: 'Bericht versleutelen',
        order: {
          title: 'Bestellen',
          domains: 'Domeinnamen',
          certificates: 'Certificaten',
        },
        users: {
          title: 'Gebruikers',
          roles: 'Rollen',
        },
        tickets: {
          title: 'Tickets',
        },
        products: {
          title: 'Producten',
        },
        finance: {
          title: 'Financieel',
          invoices: 'Facturen',
        },
        domains: {
          title: 'Domeinnamen',
          handles: 'Handles',
        },
        certificates: {
          title: 'Certificaten',
        },
        servers: {
          title: 'Servers',
        },
        dns: {
          title: 'DNS',
          bulkActions: 'Bulk acties',
        },
        apps: {
          title: 'Apps',
        },
        secrets: {
          title: 'Secrets',
        },
        documents: {
          title: 'Documenten',
        },
        actions: {
          title: 'Acties',
        },
        access: {
          ipWhitelist: 'IP-toegangslijst',
          customerPreferences: 'Klantvoorkeuren',
        },
        dashboard: {
          title: 'Dashboard',
        },
        settings: {
          title: 'Instellingen',
          appearance: 'Weergave',
          preferences: 'Voorkeuren',
          security: 'Beveiliging',
          apiAccess: 'API Toegang',
          switchCustomer: 'Klant wisselen',
        },
      },
    },
    modals: {
      supportToken: {
        create: {
          title: 'Support token toevoegen',
          information:
            'Een support token kan worden gebruikt om ondersteuning te krijgen zonder dat je een geautoriseerde contactpersoon bent. Als je ons belt vanaf een telefoonnummer of e-mailt vanaf een e-mailadres dat niet bij ons bekend is, kun je met behulp van een support token toch ondersteuning ontvangen van Exonet.',
          description: 'Omschrijving',
          expiresAt: 'Verloopt op',
        },
        show: {
          tokenBelow: 'Jouw token is hieronder weergegeven en verloopt op ',
          copy: 'Kopieer token',
        },
      },
      secureMessage: {
        encrypt: {
          title: 'Versleutel een bericht',
          information:
            'Een versleuteld bericht is een veilige manier om gevoelige data zoals wachtwoorden, tokens en certificaten naar Exonet te sturen. Je bericht wordt versleuteld opgeslagen en kan zonder een sleutel niet bekeken worden. Versleutelde berichten worden maar voor een korte periode opgeslagen en daarna automatisch verwijderd.',
          displayInformation:
            'Je bericht is versleuteld opgeslagen. Wij kunnen dit bericht alleen lezen als je ons de volgende sleutel verstrekt.',
          expiresAt: 'Verloopt op',
          message: 'Bericht',
          copyContent: 'Kopieer bericht',
          computedMessage:
            '#### Titel\n**Gebruikersnaam**: `root`  \n**Wachtwoord**: `{{password}}`',
          shareableText: 'Verificatiecode: {{code}}\nGeldig tot: {{date}}',
        },
      },
    },
    validation: {
      number: 'Dit veld moet een nummer zijn',
      maxChars: 'Het maximaal aantal karakters voor dit veld is {{amount}}',
      required: 'Dit veld is verplicht',
      inFuture: 'De datum moet in de toekomst zijn',
      validEmail: 'Vul een geldig e-mailadres in',
      validLanguage: 'Kies een taal',
      validPhone: 'Vul een geldig telefoonnummer in',
      validDate: 'Vul een geldige datum in',
      validValue: 'De geselecteerde waarde is niet geldig',
      validIp: 'Vul een geldig IP-adres in',
      validZipCode: 'Vul een geldige postcode in',
      minValue: 'De minimale waarde is {{min}}',
    },
    footer: {
      copyright: '© Exonet B.V. Alle genoemde prijzen zijn exclusief 21% BTW.',
      iso: 'Exonet is ISO 9001, ISO 27001 en NEN 7510 gecertificeerd.',
      link: {
        status: {
          href: 'https://www.exonetstatus.com',
          title: 'Status',
        },
        terms: {
          href: 'https://www.exonet.nl/algemene-voorwaarden',
          title: 'Algemene voorwaarden',
        },
        privacy: {
          href: 'https://www.exonet.nl/privacy-verklaring',
          title: 'Privacy verklaring',
        },
      },
    },
    dataManager: {
      title: 'Overzicht',
      search: {
        placeholder: 'Zoeken op..',
      },
      footer: {
        pagination: 'Toon $from tot $to van $total.',
        info: 'Toon $amount van $total.',
      },
      noData: 'Geen resultaten gevonden.',
      noDataForType: 'Geen {{type}} gevonden.',
      dateRange: {
        fromDate: 'Van datum',
        toDate: 'Tot datum',
      },
      selectCountry: 'Selecteer een land',
      export: {
        idle: 'Export',
        loading: 'Bezig met exporteren…',
        success: 'Export voltooid',
        error: 'Export mislukt',
      },
    },
    countries: {
      ad: 'Andorra',
      ae: 'Verenigde Arabische Emiraten',
      af: 'Afghanistan',
      ag: 'Antigua en Barbuda',
      ai: 'Anguilla',
      al: 'Albanië',
      am: 'Armenië',
      an: 'Nederlandse Antillen',
      ao: 'Angola',
      aq: 'Antarctica',
      ar: 'Argentinië',
      as: 'Amerikaans-Samoa',
      at: 'Oostenrijk',
      au: 'Australië',
      aw: 'Aruba',
      ax: 'Åland-eilanden',
      az: 'Azerbeidzjan',
      ba: 'Bosnië en Herzegovina',
      bb: 'Barbados',
      bd: 'Bangladesh',
      be: 'België',
      bf: 'Burkina Faso',
      bg: 'Bulgarije',
      bh: 'Bahrein',
      bi: 'Burundi',
      bj: 'Benin',
      bl: 'Saint-Barthélemy',
      bm: 'Bermuda',
      bn: 'Brunei',
      bo: 'Bolivia',
      bq: 'Bonaire',
      br: 'Brazilië',
      bs: 'Bahama’s',
      bt: 'Bhutan',
      bv: 'Bouveteiland',
      bw: 'Botswana',
      by: 'Wit-Rusland',
      bz: 'Belize',
      ca: 'Canada',
      cc: 'Cocoseilanden',
      cd: 'Congo-Kinshasa',
      cf: 'Centraal-Afrikaanse Republiek',
      cg: 'Congo-Brazzaville',
      ch: 'Zwitserland',
      ci: 'Ivoorkust',
      ck: 'Cookeilanden',
      cl: 'Chili',
      cm: 'Kameroen',
      cn: 'China',
      co: 'Colombia',
      cr: 'Costa Rica',
      cu: 'Cuba',
      cv: 'Kaapverdië',
      cw: 'Curaçao',
      cx: 'Christmaseiland',
      cy: 'Cyprus',
      cz: 'Tsjechië',
      de: 'Duitsland',
      dj: 'Djibouti',
      dk: 'Denemarken',
      dm: 'Dominica',
      do: 'Dominicaanse Republiek',
      dz: 'Algerije',
      ec: 'Ecuador',
      ee: 'Estland',
      eg: 'Egypte',
      eh: 'Westelijke Sahara',
      er: 'Eritrea',
      es: 'Spanje',
      et: 'Ethiopië',
      fi: 'Finland',
      fj: 'Fiji',
      fk: 'Falklandeilanden',
      fm: 'Micronesië',
      fo: 'Faeröer',
      fr: 'Frankrijk',
      ga: 'Gabon',
      gb: 'Verenigd Koninkrijk',
      gd: 'Grenada',
      ge: 'Georgië',
      gf: 'Frans-Guyana',
      gg: 'Guernsey',
      gh: 'Ghana',
      gi: 'Gibraltar',
      gl: 'Groenland',
      gm: 'Gambia',
      gn: 'Guinee',
      gp: 'Guadeloupe',
      gq: 'Equatoriaal-Guinea',
      gr: 'Griekenland',
      gt: 'Guatemala',
      gu: 'Guam',
      gw: 'Guinee-Bissau',
      gy: 'Guyana',
      hk: 'Hongkong',
      hm: 'Heard en McDonaldeilanden',
      hn: 'Honduras',
      hr: 'Kroatië',
      ht: 'Haïti',
      hu: 'Hongarije',
      id: 'Indonesië',
      ie: 'Ierland',
      il: 'Israël',
      im: 'Isle of Man',
      in: 'India',
      io: 'Brits Indische Oceaanterritorium',
      iq: 'Irak',
      ir: 'Iran',
      is: 'IJsland',
      it: 'Italië',
      je: 'Jersey',
      jm: 'Jamaica',
      jo: 'Jordanië',
      jp: 'Japan',
      ke: 'Kenia',
      kg: 'Kirgizië',
      kh: 'Cambodja',
      ki: 'Kiribati',
      km: 'Comoren',
      kn: 'Saint Kitts en Nevis',
      kp: 'Noord-Korea',
      kr: 'Zuid-Korea',
      kw: 'Koeweit',
      ky: 'Kaaimaneilanden',
      kz: 'Kazachstan',
      la: 'Laos',
      lb: 'Libanon',
      lc: 'Saint Lucia',
      li: 'Liechtenstein',
      lk: 'Sri Lanka',
      lr: 'Liberia',
      ls: 'Lesotho',
      lt: 'Litouwen',
      lu: 'Luxemburg',
      lv: 'Letland',
      ly: 'Libië',
      ma: 'Marokko',
      mc: 'Monaco',
      md: 'Moldavië',
      me: 'Montenegro',
      mf: 'Saint-Martin',
      mg: 'Madagaskar',
      mh: 'Marshalleilanden',
      mk: 'Noord-Macedonië',
      ml: 'Mali',
      mm: 'Myanmar',
      mn: 'Mongolië',
      mo: 'Macau',
      mp: 'Noordelijke Marianen',
      mq: 'Martinique',
      mr: 'Mauritanië',
      ms: 'Montserrat',
      mt: 'Malta',
      mu: 'Mauritius',
      mv: 'Maldiven',
      mw: 'Malawi',
      mx: 'Mexico',
      my: 'Maleisië',
      mz: 'Mozambique',
      na: 'Namibië',
      nc: 'Nieuw-Caledonië',
      ne: 'Niger',
      nf: 'Norfolk',
      ng: 'Nigeria',
      ni: 'Nicaragua',
      nl: 'Nederland',
      no: 'Noorwegen',
      np: 'Nepal',
      nr: 'Nauru',
      nu: 'Niue',
      nz: 'Nieuw-Zeeland',
      om: 'Oman',
      pa: 'Panama',
      pe: 'Peru',
      pf: 'Frans-Polynesië',
      pg: 'Papoea-Nieuw-Guinea',
      ph: 'Filipijnen',
      pk: 'Pakistan',
      pl: 'Polen',
      pm: 'Saint-Pierre en Miquelon',
      pn: 'Pitcairneilanden',
      pr: 'Puerto Rico',
      ps: 'Palestina',
      pt: 'Portugal',
      pw: 'Palau',
      py: 'Paraguay',
      qa: 'Qatar',
      re: 'Réunion',
      ro: 'Roemenië',
      rs: 'Servië',
      ru: 'Rusland',
      rw: 'Rwanda',
      sa: 'Saoedi-Arabië',
      sb: 'Salomonseilanden',
      sc: 'Seychellen',
      vg: 'Britse Maagdeneilanden',
      tf: 'Franse Zuidelijke en Antarctische Gebieden',
      xk: 'Kosovo',
      yt: 'Mayotte',
      vc: 'Saint Vincent en de Grenadines',
      ws: 'Samoa',
      sx: 'Sint Maarten',
      za: 'Zuid-Afrika',
      sj: 'Spitsbergen en Jan Mayen',
      tw: 'Taiwan',
      tz: 'Tanzania',
      tt: 'Trinidad en Tobago',
      tv: 'Tuvalu',
      um: 'Kleine Afgelegen Eilanden van de Verenigde Staten',
      vi: 'Amerikaanse Maagdeneilanden',
      ug: 'Uganda',
      ua: 'Oekraïne',
      us: 'Verenigde Staten',
      uy: 'Uruguay',
      uz: 'Oezbekistan',
      vu: 'Vanuatu',
      va: 'Vaticaanstad',
      ve: 'Venezuela',
      vn: 'Vietnam',
      wf: 'Wallis en Futuna',
      ye: 'Jemen',
      zm: 'Zambia',
      zw: 'Zimbabwe',
    },
    select: {
      placeholder: 'Selecteer een optie',
      noOptionsFound: 'Geen opties gevonden',
    },
  },
};
